/**
 * FloatingToolbarPlugin
 *
 * Menu flottant qui apparaît au-dessus de toute sélection de texte
 * dans l'éditeur Lexical. Rendu via createPortal pour éviter le clipping.
 *
 * Actions disponibles :
 * - Gras / Italique / Code inline
 * - Titres H1 / H2 / H3 (toggle : re-clic → paragraphe)
 * - Liste à puces / numérotée / cases à cocher
 *
 * Comportement :
 * - Apparaît dès qu'une sélection non-collapsed existe
 * - Se positionne au-dessus de la sélection, centré horizontalement
 * - Disparaît au clic extérieur ou quand la sélection s'efface
 * - onMouseDown e.preventDefault() sur le toolbar pour ne pas perdre la sélection
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  FORMAT_TEXT_COMMAND,
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $createHeadingNode, $isHeadingNode, type HeadingTagType } from '@lexical/rich-text';
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
} from '@lexical/list';
import { $getNearestNodeOfType } from '@lexical/utils';
import { Bold, Italic, Code, List, ListOrdered, CheckSquare } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Icon } from '@/shared/ui';

// ─── Types ────────────────────────────────────────────────────────────────────

type BlockType = 'paragraph' | 'h1' | 'h2' | 'h3' | 'bullet' | 'number' | 'check';

interface ToolbarState {
  isBold: boolean;
  isItalic: boolean;
  isCode: boolean;
  blockType: BlockType;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns the bounding rect of the current native selection, or null */
const getSelectionRect = (): DOMRect | null => {
  const nativeSel = window.getSelection();
  if (!nativeSel || nativeSel.rangeCount === 0) return null;
  const range = nativeSel.getRangeAt(0);
  const rect = range.getBoundingClientRect();
  // Collapsed rect has zero width — treat as no selection
  return rect.width > 0 ? rect : null;
};

// ─── Component ────────────────────────────────────────────────────────────────

const TOOLBAR =
  'bg-bg border-border fixed z-(--z-floating) flex -translate-x-1/2 items-center gap-px rounded-md border px-[5px] py-[3px] shadow-lg [animation:toolbarIn_var(--transition-fast)_ease_forwards]';

const BTN =
  'text-fg-secondary hover:bg-bg-hover hover:text-fg flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-sm border border-transparent bg-transparent transition-colors';

const BTN_ACTIVE =
  'bg-(--color-accent-bg) border-(--color-accent-border) text-primary hover:bg-(--color-primary-bg-subtle) hover:border-(--color-primary-lighter) hover:text-(--color-primary-hover)';

export const FloatingToolbarPlugin = () => {
  const [editor] = useLexicalComposerContext();

  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [state, setState] = useState<ToolbarState>({
    isBold: false,
    isItalic: false,
    isCode: false,
    blockType: 'paragraph',
  });

  const toolbarRef = useRef<HTMLDivElement>(null);

  // ─── Read current selection state ─────────────────────────────────────────

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if (!$isRangeSelection(selection) || selection.isCollapsed()) {
      setVisible(false);
      return;
    }

    // ── Text format
    const isBold = selection.hasFormat('bold');
    const isItalic = selection.hasFormat('italic');
    const isCode = selection.hasFormat('code');

    // ── Block type — walk up to top-level element
    let blockType: BlockType = 'paragraph';
    const anchorNode = selection.anchor.getNode();
    const topElement =
      anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();

    if ($isHeadingNode(topElement)) {
      blockType = topElement.getTag() as BlockType;
    } else if ($isListNode(topElement)) {
      blockType = topElement.getListType();
    } else {
      const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
      if (parentList) blockType = parentList.getListType();
    }

    setState({ isBold, isItalic, isCode, blockType });

    // ── Position above the selection
    const rect = getSelectionRect();
    if (rect) {
      const TOOLBAR_H = 40;
      const OFFSET = 8;
      const topAbove = rect.top - TOOLBAR_H - OFFSET;
      const topBelow = rect.bottom + OFFSET;
      setPosition({
        top: topAbove >= 4 ? topAbove : topBelow,
        left: rect.left + rect.width / 2,
      });
      setVisible(true);
    }
  }, []);

  // ─── Register Lexical listeners ───────────────────────────────────────────
  // registerUpdateListener covers all cases: selection changes AND content
  // changes (e.g. bold toggle updating toolbar active state). A single listener
  // is sufficient — no need for a redundant SELECTION_CHANGE_COMMAND handler.

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(updateToolbar);
    });
  }, [editor, updateToolbar]);

  // ─── Hide on outside click ────────────────────────────────────────────────

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        setVisible(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, []);

  // ─── Format actions ───────────────────────────────────────────────────────

  const formatInline = (format: 'bold' | 'italic' | 'code') => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const formatHeading = (tag: HeadingTagType) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      // Toggle: same heading → back to paragraph
      if (state.blockType === tag) {
        $setBlocksType(selection, () => $createParagraphNode());
      } else {
        $setBlocksType(selection, () => $createHeadingNode(tag));
      }
    });
  };

  const formatList = (type: 'bullet' | 'number' | 'check') => {
    if (type === 'bullet') editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    else if (type === 'number') editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    else editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (!visible) return null;

  return createPortal(
    <div
      ref={toolbarRef}
      className={TOOLBAR}
      style={{ top: position.top, left: position.left }}
      // Critical: prevent editor from losing focus/selection on toolbar click
      onMouseDown={(e) => {
        e.preventDefault();
      }}
    >
      {/* Inline formatting */}
      <button
        type="button"
        className={cn(BTN, state.isBold && BTN_ACTIVE)}
        onClick={() => {
          formatInline('bold');
        }}
        title="Gras (Ctrl+B)"
      >
        <Icon icon={Bold} size="md" strokeWidth={2.25} />
      </button>
      <button
        type="button"
        className={cn(BTN, state.isItalic && BTN_ACTIVE)}
        onClick={() => {
          formatInline('italic');
        }}
        title="Italique (Ctrl+I)"
      >
        <Icon icon={Italic} size="md" strokeWidth={2.25} />
      </button>
      <button
        type="button"
        className={cn(BTN, state.isCode && BTN_ACTIVE)}
        onClick={() => {
          formatInline('code');
        }}
        title="Code inline"
      >
        <Icon icon={Code} size="md" strokeWidth={2.25} />
      </button>

      <div className="bg-border mx-[3px] h-4.5 w-px shrink-0" />

      {/* Headings */}
      <button
        type="button"
        className={cn(BTN, state.blockType === 'h1' && BTN_ACTIVE)}
        onClick={() => {
          formatHeading('h1');
        }}
        title="Titre H1"
      >
        <span className="text-xs leading-none font-bold tracking-tighter">H1</span>
      </button>
      <button
        type="button"
        className={cn(BTN, state.blockType === 'h2' && BTN_ACTIVE)}
        onClick={() => {
          formatHeading('h2');
        }}
        title="Titre H2"
      >
        <span className="text-xs leading-none font-bold tracking-tighter">H2</span>
      </button>
      <button
        type="button"
        className={cn(BTN, state.blockType === 'h3' && BTN_ACTIVE)}
        onClick={() => {
          formatHeading('h3');
        }}
        title="Titre H3"
      >
        <span className="text-xs leading-none font-bold tracking-tighter">H3</span>
      </button>

      <div className="bg-border mx-[3px] h-4.5 w-px shrink-0" />

      {/* Lists */}
      <button
        type="button"
        className={cn(BTN, state.blockType === 'bullet' && BTN_ACTIVE)}
        onClick={() => {
          formatList('bullet');
        }}
        title="Liste à puces"
      >
        <Icon icon={List} size="md" strokeWidth={2.25} />
      </button>
      <button
        type="button"
        className={cn(BTN, state.blockType === 'number' && BTN_ACTIVE)}
        onClick={() => {
          formatList('number');
        }}
        title="Liste numérotée"
      >
        <Icon icon={ListOrdered} size="md" strokeWidth={2.25} />
      </button>
      <button
        type="button"
        className={cn(BTN, state.blockType === 'check' && BTN_ACTIVE)}
        onClick={() => {
          formatList('check');
        }}
        title="Liste de cases à cocher"
      >
        <Icon icon={CheckSquare} size="md" strokeWidth={2.25} />
      </button>
    </div>,
    document.body,
  );
};
